import React, { useEffect, useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, ChevronDown } from 'lucide-react';
import {
  SIGN_IN_DISCLAIMER_ACKNOWLEDGEMENT,
  SIGN_IN_DISCLAIMER_INTRO,
  SIGN_IN_DISCLAIMER_SECTIONS,
  SIGN_IN_DISCLAIMER_VERSION,
} from './signInDisclaimerContent';

export default function SignInDisclaimerModal({ open, onAccept }: { open: boolean; onAccept: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) return;
    setScrolled(false);
    setChecked(false);
  }, [open]);

  if (!open) return null;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) setScrolled(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#022c22]/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg bg-[#022c22] flex items-center justify-center">
            <Shield size={15} className="text-emerald-300" />
          </div>
          <div>
            <h2 className="text-[14px] font-medium text-gray-900">Terms of Use & Disclaimer</h2>
            <p className="text-[9px] text-gray-400 mt-0.5">Please review and acknowledge before continuing</p>
          </div>
        </div>

        <div onScroll={handleScroll} className="px-6 py-5 max-h-[400px] overflow-y-auto space-y-4">
          <div className="bg-amber-50/60 border border-amber-100 rounded-lg px-4 py-3 flex items-start space-x-2.5">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-amber-800 leading-relaxed font-medium">{SIGN_IN_DISCLAIMER_INTRO}</p>
          </div>

          <div className="space-y-3">
            {SIGN_IN_DISCLAIMER_SECTIONS.map((section, i) => (
              <div key={section.title}>
                <h3 className="text-[11px] font-medium text-gray-900 mb-1">
                  {i + 1}. {section.title}
                </h3>
                <p className="text-[10px] text-gray-600 leading-relaxed">{section.body}</p>
              </div>
            ))}
          </div>

          {!scrolled && (
            <div className="flex justify-center pt-2">
              <div className="flex items-center space-x-1 text-[9px] text-gray-400 animate-bounce">
                <ChevronDown size={10} />
                <span>Scroll to continue</span>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 space-y-3">
          <label
            className={`flex items-start space-x-3 cursor-pointer ${!scrolled ? 'opacity-40 pointer-events-none' : ''}`}
            onClick={() => scrolled && setChecked(!checked)}
          >
            <div
              className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${checked ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}
            >
              {checked && <CheckCircle size={10} className="text-white" />}
            </div>
            <span className="text-[10px] text-gray-700 leading-relaxed">{SIGN_IN_DISCLAIMER_ACKNOWLEDGEMENT}</span>
          </label>

          <div className="flex items-center justify-between">
            <p className="text-[8px] text-gray-400">{SIGN_IN_DISCLAIMER_VERSION}</p>
            <button
              onClick={onAccept}
              disabled={!checked}
              className="inline-flex items-center px-5 py-2 rounded-md text-[11px] font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              I Acknowledge — Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
